import { CHECKED_STATUS } from '../../../services/search/search.service';
import { MatLegacyTableDataSource as MatTableDataSource } from '@angular/material/legacy-table';
import {
  Component,
  OnChanges,
  ViewChild,
  SimpleChanges,
  Input,
  AfterViewInit,
  Output,
  EventEmitter,
  ContentChild,
  Pipe,
  PipeTransform,
  OnDestroy,
} from '@angular/core';
import { MatSort } from '@angular/material/sort';
import {
  MatLegacyPaginator as MatPaginator,
  LegacyPageEvent as PageEvent,
} from '@angular/material/legacy-paginator';
import { coerceBooleanProperty } from '@angular/cdk/coercion';

export function getValueByPath(obj: any, path: string): any {
  return path.split('.').reduce((current, part) => current && current[part], obj);
}

/** Table column data format */
export interface TableGridColumn {
  /** Column Label */
  label: string;
  /** Property name or path. ie, id or library.id */
  value: string;
  /** Display Column */
  display: boolean;
  /**
   * Include cell value in filter
   * @description If true, the column will be included in the filter
   */
  filter?: boolean;
  /**
   * Sort by this columns cell value instead of this columns sort value
   * @param value - The property value,
   * @param data - The full row data
   * @returns The formatted string to display
   */
  sortKeyFn?: (value: any, data: any) => string;
  /**
   * Custom display function for the cell value
   * @param value - The property value,
   * @param data - The full row data
   * @returns The formatted string to display
   */
  displayFn?: (value: any, data: any) => string;
  /**
   * Custom filter function for the cell value
   * @example filterFn: (term: string, value: string) => value.toLowerCase().includes(term.toLowerCase()),
   * @param term
   * @param data
   * @returns
   */
  filterFn?: (term: string, data: any) => boolean;
  /**
   * Custom click function for the cell value
   * @param data
   * @returns
   */
  clickFn?: (data: any) => void;
  tooltip?: (data: any) => string;
}

/**
 * Table action button
 * @description Table level action button option to toggle enable and disable when showSpinner or multiSelect is enabled
 */
export interface TableAction extends RowAction {
  canDisable: boolean;
}

/** Table row level actions for the dropdown list */
export interface RowAction {
  type: string;
  label: string;
}

/** Output format emitted from this component */
export interface SelectedActionOutput<T> {
  type: string;
  data: T | T[];
  displayedColumns: string[];
}

@Pipe({ name: 'cellDisplayFn' })
export class CellDisplayFunctionPipe implements PipeTransform {
  constructor() {}
  transform(data: any, column: TableGridColumn): string {
    const value = getValueByPath(data, column.value);
    return column.displayFn ? column.displayFn(value, data) : value;
  }
}

// TODO: This should bbe removed and replaced with the displayFn
@Pipe({ name: 'cellConvertArray' })
export class CellConvertArrayPipe implements PipeTransform {
  constructor() {}
  transform(value: string | string[]): string {
    return Array.isArray(value) ? value[0] : value;
  }
}

/**
 * Table Grid Component
 * @description table grid to render data which supports filtering, sorting, paging and actions to be performed on individual rows / entire table
 */
@Component({
  selector: 'ag-table-grid[data][displayedColumns][noDataMessage]',
  templateUrl: './table-grid.component.html',
  styleUrls: ['./table-grid.component.scss'],
})
export class TableGridComponent<T> implements OnChanges, AfterViewInit, OnDestroy {
  static CHECKBOX = 'checkbox';
  static ACTIONS = 'actions';
  /** The data that is displayed in the table */
  @Input() data: T[] = null;
  /** Table headers */
  @Input() displayedColumns: TableGridColumn[];
  /** Spinner on the table level action buttons that the click action performed on */
  @Input() showSpinner: boolean;
  /** Initializes the specified column for sorting by the TableGridColumn's value in either ascending (asc) or descending (desc) order */
  @Input() initialSort: { id: string; start: 'desc' | 'asc' };
  @Input() stickyTopOffset: number = 50;
  /** Controls whether or not the filter header is sticky */
  @Input() get stickyFilterHeader() {
    return this._stickyFilterHeader;
  }
  set stickyFilterHeader(value: any) {
    this._stickyFilterHeader = coerceBooleanProperty(value);
  }

  /** Controls whether or not the column headers are sticky */
  @Input() get stickyColumnHeader() {
    return this._stickyColumnHeader;
  }
  set stickyColumnHeader(value: any) {
    this._stickyColumnHeader = coerceBooleanProperty(value);
  }

  /** Controls whether or not the footer(paginator) is sticky */
  @Input() get stickyFooter() {
    return this._stickyFooter;
  }
  set stickyFooter(value: any) {
    this._stickyFooter = coerceBooleanProperty(value);
  }

  /** If enabled will emit row data on click */
  @Input() get rowSelection() {
    return this._rowSelection;
  }
  set rowSelection(value: any) {
    this._rowSelection = coerceBooleanProperty(value);
  }

  /** Message to display in table when no results */
  @Input() noDataMessage: string;
  /** Controls checkboxes and allows multiple rows to be selected to perform table level actions */
  @Input() multiSelect = true;
  /** Number of results displayed in table */
  @Input() pageSize = 50;
  @Input() pageIndex = 0;
  /** Options for count of results displayed in the table */
  @Input() pageSizeOptions = [10, 25, 50, 100, 250];
  /** Table level actions, buttons on the top right of the table */
  @Input() tableActions: TableAction[];
  /** Filter column button icon */
  @Input() displayFilterColumns = true;
  /** Populates the vertical ellipsis in each row, will display if has length */
  @Input() rowActions: RowAction[];
  /** The length of the total number of items that are being paginated */
  @Input() length = 0;
  /** Option to hide the paginator */
  @Input() showPagination = true;
  /** Populates  */
  @Input() filter = '';
  /** When false, column sort headers are hidden and no client-side sorting is applied */
  @Input() sortable: boolean = true;
  /**
   * Filter mode for the table
   * - 'none': No top bar at all
   * - 'quick': Filter input on left, action buttons on right
   * - 'column': No top bar, filters in column headers
   * - 'actions': Only action buttons, right-aligned
   */
  @Input() filterMode: 'none' | 'quick' | 'column' | 'actions' = 'none';
  @ContentChild('rowActionIcons') rowActionIcons: any;
  /** Emitted output on table level action buttons, both table and rows */
  @Output() tableAction = new EventEmitter<SelectedActionOutput<T>>();
  /** Emitted output on row level action buttons, both table and rows */
  @Output() rowAction = new EventEmitter<SelectedActionOutput<T>>();
  @Output() filterChanged = new EventEmitter<string>();
  @Output() columnsChanged = new EventEmitter<TableGridColumn[]>();
  @Output() pageChanged = new EventEmitter<PageEvent>();
  @ViewChild(MatSort) sort: MatSort;
  @ViewChild(MatPaginator) paginator: MatPaginator;

  checkedStatus = CHECKED_STATUS;
  checkBoxStatus = CHECKED_STATUS.NONE;
  checkbox = TableGridComponent.CHECKBOX;
  actions = TableGridComponent.ACTIONS;
  dataSource = new MatTableDataSource(this.data);
  columns: string[];
  actionIndex: number;
  columnFilterValues: { [key: string]: string } = {};

  private _selectAll = false;
  private _stickyFilterHeader = false;
  private _stickyColumnHeader = false;
  private _stickyFooter = false;
  private _rowSelection = false;

  ngOnChanges({ data, displayedColumns }: SimpleChanges): void {
    if (data?.currentValue) {
      this._setupDataSource(data.currentValue);
    }

    if (displayedColumns?.currentValue) {
      this._initializeTable(displayedColumns?.currentValue);
    }
  }

  ngAfterViewInit() {
    if (this.filter) {
      this.applyFilter(this.filter);
    }

    this._setupSortAndPaginator();
    this._changeCssVariable('--table-sticky-top-offset', `${this.stickyTopOffset}px`);
  }

  ngOnDestroy(): void {
    this._changeCssVariable('--table-sticky-top-offset', '50px');
  }

  private _initializeTable(displayedColumns) {
    this._addCheckboxesToColumns();
    this._attachActionsToColumns();
    this.setupColumns(displayedColumns);
    this._setupDataSource(this.data);
  }

  applyColumnFilters(filters: { [key: string]: string }) {
    this.dataSource.filterPredicate = (row: any, filter: string) => {
      const searchTerms = JSON.parse(filter);
      return Object.entries(searchTerms).every(([key, val]) =>
        row[key]
          ?.toString()
          .toLowerCase()
          .includes((val as string).toLowerCase()),
      );
    };

    this.dataSource.filter = JSON.stringify(filters);
  }

  onColumnKeyup(event: KeyboardEvent, columnKey: string) {
    const input = event.target as HTMLInputElement;
    this.columnFilterValues[columnKey] = input.value;
    this.applyColumnFilters(this.columnFilterValues);
  }

  clearColumnFilter(columnKey: string) {
    this.columnFilterValues[columnKey] = '';
    this.applyColumnFilters(this.columnFilterValues);
  }

  private _changeCssVariable(variable: string, value: string) {
    document.documentElement.style.setProperty(variable, value);
  }

  /**
   * @description takes displayedColumns and creates the columns that populates the table headers
   * based on display being true or false
   */
  setupColumns(displayedColumns) {
    this.columns = displayedColumns.map((c) => c.value);
  }

  // if multiSelect is on, will add checkboxes to the table
  private _addCheckboxesToColumns() {
    if (this.multiSelect) {
      this.displayedColumns.unshift({
        value: TableGridComponent.CHECKBOX,
        label: TableGridComponent.CHECKBOX,
        display: true,
      });
    }
  }

  // sets up the actions column in the table
  private _attachActionsToColumns() {
    if (!this.displayedColumns.some((column) => column.value === TableGridComponent.ACTIONS)) {
      this.displayedColumns.push({
        value: TableGridComponent.ACTIONS,
        label: TableGridComponent.ACTIONS,
        display: true,
      });
    }
  }

  private _setupDataSource(dataSource) {
    this.dataSource = new MatTableDataSource(dataSource);
    // setup filter predicate to filter on displayed columns instead of all
    this.dataSource.filterPredicate = (data: any, filter: string) => {
      for (const column of this.displayedColumns) {
        let value = getValueByPath(data, column.value);

        if (column.filterFn) {
          return column.filterFn(filter, value);
        } else if (column.displayFn) {
          value = column.displayFn(value, data);
        }
        if (value?.toString().toLowerCase().includes(filter.toLowerCase())) {
          return true;
        }
      }
    };

    this.dataSource.sortingDataAccessor = (data: any, sortHeaderId: string): string | number => {
      const column = this.displayedColumns.find((col) => col.value === sortHeaderId);

      sortHeaderId = column.sortKeyFn ? column.sortKeyFn(column.value, data) : sortHeaderId;

      let value: any;

      if (column && column.displayFn) {
        // DisplayFn
        const orgValue = getValueByPath(data, sortHeaderId);
        value = column.displayFn(orgValue ?? '', data);
      } else {
        // Fallback
        value = getValueByPath(data, sortHeaderId);
      }

      // Handle date strings - M/D/YYYY or MM/DD/YYYY, optionally with HH:mm am/pm time
      if (
        typeof value === 'string' &&
        /^\d{1,2}\/\d{1,2}\/\d{4}(\s+\d{1,2}:\d{2}\s*(am|pm))?$/i.test(value)
      ) {
        const parsedDate = new Date(value.replace(/(am|pm)/i, (m) => m.toUpperCase()));
        if (!isNaN(parsedDate.getTime())) {
          return parsedDate.getTime();
        }
      }

      // setup case insensitive sorting
      return typeof value === 'string' ? value.toUpperCase() : value;
    };

    this._setupSortAndPaginator();
  }

  private _setupSortAndPaginator() {
    if (this.sortable) {
      this.dataSource.sort = this.sort;

      if (this.sort && this.initialSort) {
        // Ensure the sort state is reset to the initial sort state
        this.sort.active = this.initialSort.id;
        this.sort.direction = this.initialSort.start;
        this.sort.sortChange.emit({ active: this.sort.active, direction: this.sort.direction });
      }
    }

    if (!this.length) {
      this.dataSource.paginator = this.paginator;
    }
  }

  applyFilter(filter: string): void {
    this.dataSource.filter = filter.toLowerCase();
    this.filterChanged.emit(filter);
  }

  toggleAllRowsSelected(): void {
    this._selectAll = !this._selectAll;
    this.dataSource.data.forEach((x) => (x['selected'] = this._selectAll));
    this.checkBoxStatus = this._selectAll ? CHECKED_STATUS.ALL : CHECKED_STATUS.NONE;
  }

  /**
   * @description Used to determine the state of the checkbox in the column header,
   * can only be interacted with if multiselect is enabled
   */
  checkIndeterminateStatus(): void {
    const selectedRows = this.getSelectedRows();

    if (selectedRows.length === this.dataSource.data.length) {
      this.checkBoxStatus = CHECKED_STATUS.ALL;
    } else if (selectedRows.length) {
      this.checkBoxStatus = CHECKED_STATUS.PARTIAL;
      this._selectAll = false;
    } else {
      this.checkBoxStatus = CHECKED_STATUS.NONE;
    }
  }

  /**
   * @description Used to toggle the selection of multiple rows, can only be interacted
   * with if multiselect is enabled
   */
  toggleRow(row: T): void {
    if (this.multiSelect) {
      row['selected'] = !row['selected'];
      this.checkIndeterminateStatus();
    }

    if (this.rowSelection) {
      row['selected'] = !row['selected'];
      this.rowActionSelect('rowSelected', row);
    }
  }

  private getSelectedRows(): T[] {
    return this.dataSource.data.filter((x) => x['selected']);
  }

  /**
   * @description clears the text in the filter input box and resets table to default
   * state after data has been rendered
   */
  clearFilter(): void {
    this.filter = '';
    this.dataSource.filter = '';
    this.filterChanged.emit('');
  }

  /**
   * Emits the action type and the row data that are selected
   * @param action the action type based on the table action button selected
   * @param index the specific button that was selected to render the spinner on
   */
  tableClickAction(action: string, index: number) {
    this.actionIndex = index;
    const selectedRows = this.getSelectedRows();
    this.tableAction.emit({
      type: action,
      data: selectedRows,
      displayedColumns: this.displayedColumns
        .filter((c) => c.display && c.value !== TableGridComponent.ACTIONS)
        .map((c) => c.value),
    });
  }

  /**
   * Emits the action type and the row dat that was selected
   * @param action the action type based on the table action button selected
   * @param row the data of the row action was performed on
   */
  rowActionSelect(action: string, row: any) {
    this.rowAction.emit({
      type: action,
      data: row,
      displayedColumns: this.displayedColumns
        .filter((c) => c.display && c.value !== TableGridComponent.ACTIONS)
        .map((c) => c.value),
    });
  }

  onPageEvent(event: PageEvent): void {
    this.pageChanged.emit(event);
  }
}
